import { AlertTriangle, ExternalLink, MessageSquarePlus, PlusCircle } from "lucide-react";
import { Textarea } from "../ui/input";
import { cn } from "../../utils/tailwindUtils";
import { getStageBadgeClass } from "../../utils/leadStageBadge";
import { formatDate } from "../../utils/datetime";

// The Add Lead form's "this number is already here" card — trustedge
// customer/dedupe/check/. Three ways out: open the existing lead, add this as
// an enquiry on it, or create a new lead anyway (with a reason, when the
// tenant's rules ask for one).

const HINT_TEXT = {
    attach: 'Adding it as an enquiry keeps one lead and tells the owner they came back.',
    reopen: 'That lead is closed. Adding it as an enquiry reopens it as New Lead with its history.',
    customer: 'This is an existing customer. If it is a new site or a new deal, create a new lead - it will be linked to them.',
};

export default function DuplicateWarning({ check, choice, reason, onReason, onOpen, onAttach, onCreateAnyway,
                                           busy }) {
    if (!check?.matches?.length) return null;
    const primary = check.matches.find(m => m.id === check.primary_id) || check.matches[0];
    const others = check.matches.filter(m => m.id !== primary.id);
    const enforced = check.is_enabled;

    return (
        <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/15 dark:border-amber-700/60 p-3 space-y-2.5">
            <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-amber-900 dark:text-amber-200">
                    <p className="font-semibold">This number is already in the CRM</p>
                    <p className="mt-0.5 text-amber-800/90 dark:text-amber-300/90">{HINT_TEXT[check.hint] || ''}</p>
                </div>
            </div>

            <MatchRow m={primary} onOpen={onOpen} highlight />
            {others.length > 0 && (
                <div className="space-y-1">
                    <p className="text-[10px] uppercase tracking-wide text-amber-700/80 dark:text-amber-400/80 font-semibold">
                        Also on this number
                    </p>
                    {others.slice(0, 4).map(m => <MatchRow key={m.id} m={m} onOpen={onOpen} />)}
                </div>
            )}

            <div className="flex flex-wrap gap-2 pt-1">
                <button type="button" disabled={busy} onClick={() => onAttach(primary)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold bg-primary text-white hover:opacity-90 disabled:opacity-50">
                    <MessageSquarePlus className="h-3.5 w-3.5" />
                    {primary.state === 'closed' ? 'Add as enquiry & reopen' : 'Add as enquiry to it'}
                </button>
                {enforced && (
                    <button type="button" disabled={busy} onClick={onCreateAnyway}
                            className={cn("inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-semibold border",
                                choice === 'create'
                                    ? "bg-white border-primary text-primary"
                                    : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600")}>
                        <PlusCircle className="h-3.5 w-3.5" /> Create a new lead anyway
                    </button>
                )}
            </div>

            {!enforced && (
                <p className="text-[11px] text-amber-800/80 dark:text-amber-300/80">
                    Saving will still create a new lead, linked to this one.
                </p>
            )}
            {enforced && choice === 'create' && (
                <div className="space-y-1">
                    <label className="text-[11px] font-medium text-gray-700 dark:text-gray-300">
                        Why does this need a new lead?{check.require_reason && <span className="text-red-500"> *</span>}
                    </label>
                    <Textarea value={reason} onChange={e => onReason(e.target.value)} rows={2} maxLength={500}
                              placeholder="e.g. Second hospital, same owner - separate deal"
                              className="w-full rounded-md border-gray-300 bg-white text-xs" />
                    <p className="text-[10px] text-gray-500">Then press Save. The new lead is linked to the one above.</p>
                </div>
            )}
        </div>
    );
}

function MatchRow({ m, onOpen, highlight }) {
    return (
        <div className={cn("flex items-center justify-between gap-2 rounded-md px-2.5 py-2 text-xs",
            highlight ? "bg-white dark:bg-gray-800 border border-amber-200 dark:border-amber-800/50" : "bg-white/60 dark:bg-gray-800/60")}>
            <div className="min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-semibold text-gray-800 dark:text-gray-100 truncate max-w-[200px]">{m.customer_name || '—'}</span>
                    {m.lead_stage && (
                        <span className={cn("px-1.5 py-0.5 rounded-full text-[9px] font-medium", getStageBadgeClass(m.lead_stage))}>{m.lead_stage}</span>
                    )}
                </div>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">
                    {m.owner ? `Owner ${m.owner}` : 'Unassigned'}
                    {m.lead_source ? ` · ${m.lead_source}` : ''}
                    {m.last_activity_at ? ` · last activity ${formatDate(m.last_activity_at)}` : ''}
                    {m.enquiry_count > 1 ? ` · enquired ${m.enquiry_count}×` : ''}
                </p>
            </div>
            {m.can_open ? (
                <button type="button" onClick={() => onOpen(m)}
                        className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline flex-shrink-0">
                    Open <ExternalLink className="h-3 w-3" />
                </button>
            ) : (
                <span className="text-[10px] text-gray-400 flex-shrink-0" title="Owned by another rep">Not yours</span>
            )}
        </div>
    );
}
